import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiSearchAPI, sitesAPI } from '../services/apiEndpoints';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import {
    RocketIcon,
    MagnifyingGlassIcon,
    CheckCircledIcon,
    CrossCircledIcon,
    InfoCircledIcon,
    DownloadIcon,
    ReloadIcon,
    LightningBoltIcon,
    FileTextIcon,
    CodeIcon,
    GlobeIcon,
    BarChartIcon,
    StackIcon,
} from '@radix-ui/react-icons';

function AISearchOptimizer({ user }) {
    // State
    const [wordPressSites, setWordPressSites] = useState([]);
    const [selectedSite, setSelectedSite] = useState('');
    const [loading, setLoading] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [scanResults, setScanResults] = useState(null);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [deploying, setDeploying] = useState(false);
    const [addingSchema, setAddingSchema] = useState(false);
    const [addingLlms, setAddingLlms] = useState(false);
    const [llmsMessage, setLlmsMessage] = useState(null);
    const [schemaMessage, setSchemaMessage] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [selectedSchemaTypes, setSelectedSchemaTypes] = useState(['WebSite']);
    const [robotsFixing, setRobotsFixing] = useState(false);
    const [robotsMessage, setRobotsMessage] = useState(null);
    const [botLoading, setBotLoading] = useState({}); // { 'GPTBot': true/false }

    const navigate = useNavigate();

    // Fetch WordPress sites on mount
    useEffect(() => {
        fetchWordPressSites();
    }, []);

    const fetchWordPressSites = async () => {
        try {
            setLoading(true);
            const response = await sitesAPI.getWordPressSites();
            const sites = response.data.sites || [];
            setWordPressSites(sites);

            if (sites.length > 0 && !selectedSite) {
                setSelectedSite(sites[0].id.toString());
            }
        } catch (err) {
            setError('Failed to load WordPress sites');
        } finally {
            setLoading(false);
        }
    };

    const handleScanSite = async () => {
        if (!selectedSite) {
            setError('Please select a WordPress site');
            return;
        }

        try {
            setScanning(true);
            setError(null);
            const response = await aiSearchAPI.scanSite(selectedSite);

            if (response.data.success) {
                const data = response.data.data;
                setScanResults(data);
                setActiveTab('results');
            } else {
                setError(response.data.message || 'Scan failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to scan site');
        } finally {
            setScanning(false);
        }
    };

    const handleGenerateLlmsTxt = async () => {
        if (!selectedSite) return;

        try {
            setLoading(true);
            const response = await aiSearchAPI.generateLlmsTxt(selectedSite);

            if (response.data.success) {
                // Update scan results with generated llms.txt
                setScanResults(prev => ({
                    ...prev,
                    generated_llms_txt: response.data.data.llms_txt,
                }));
                setActiveTab('llms-txt');
            }
        } catch (err) {
            setError('Failed to generate llms.txt');
        } finally {
            setLoading(false);
        }
    };

    const handleAddSchema = async () => {
        if (!selectedSite) return;
        try {
            setAddingSchema(true);
            setSchemaMessage(null);
            const response = await aiSearchAPI.generateSchema(selectedSite, selectedSchemaTypes);
            if (response.data.success) {
                setScanResults(prev => {
                    const scoreDelta = prev.has_schema_markup ? 0 : 25;
                    return {
                        ...prev,
                        has_schema_markup: true,
                        schema_source: 'contentvertex',
                        articles_with_schema: prev.total_articles,
                        active_schema_types: [...new Set([...(prev.active_schema_types || []), ...selectedSchemaTypes])],
                        ai_readiness_score: Math.min(100, (prev.ai_readiness_score || 0) + scoreDelta),
                    };
                });
                setSchemaMessage({ type: 'success', text: response.data.message });
            } else {
                setSchemaMessage({ type: 'error', text: response.data.message || 'Failed to enable schema.' });
            }
        } catch (err) {
            setSchemaMessage({ type: 'error', text: 'Failed to enable schema. Make sure the plugin is active.' });
        } finally {
            setAddingSchema(false);
        }
    };

    const toggleSchemaType = (type) => {
        setSelectedSchemaTypes(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
    };

    const handleAddLlmsTxt = async () => {
        if (!selectedSite) return;
        try {
            setAddingLlms(true);
            setLlmsMessage(null);
            const genResponse = await aiSearchAPI.generateLlmsTxt(selectedSite);
            if (!genResponse.data.success) throw new Error('Generation failed');
            const content = genResponse.data.data.llms_txt;
            const pushResponse = await aiSearchAPI.pushLlmsTxt(selectedSite, content);
            if (pushResponse.data.success) {
                setScanResults(prev => {
                    const scoreDelta = prev.has_llms_txt ? 0 : 20;
                    return {
                        ...prev,
                        has_llms_txt: true,
                        generated_llms_txt: content,
                        ai_readiness_score: Math.min(100, (prev.ai_readiness_score || 0) + scoreDelta),
                    };
                });
                setLlmsMessage({ type: 'success', text: 'llms.txt has been created on your WordPress site!' });
            } else {
                setLlmsMessage({ type: 'error', text: pushResponse.data.message || 'Failed to add llms.txt.' });
            }
        } catch (err) {
            setLlmsMessage({ type: 'error', text: 'Failed to add llms.txt. Make sure the plugin is active.' });
        } finally {
            setAddingLlms(false);
        }
    };

    const handleRemoveLlmsTxt = async () => {
        if (!selectedSite) return;
        try {
            setAddingLlms(true);
            setLlmsMessage(null);
            const response = await aiSearchAPI.removeLlmsTxt(selectedSite);
            if (response.data.success) {
                setScanResults(prev => ({ ...prev, has_llms_txt: false, generated_llms_txt: null }));
                setLlmsMessage({ type: 'success', text: 'llms.txt has been removed.' });
            } else {
                setLlmsMessage({ type: 'error', text: response.data.message || 'Failed to remove llms.txt.' });
            }
        } catch (err) {
            setLlmsMessage({ type: 'error', text: 'Failed to remove llms.txt: ' + (err.response?.data?.message || err.message) });
        } finally {
            setAddingLlms(false);
        }
    };

    const handleRemoveSchema = async () => {
        if (!selectedSite) return;
        try {
            setAddingSchema(true);
            setSchemaMessage(null);
            const response = await aiSearchAPI.removeSchema(selectedSite);
            if (response.data.success) {
                setSchemaMessage({ type: 'success', text: 'Schema.org markup has been disabled. Rescanning...' });
                // Rescan to get accurate active_schema_types (e.g. theme's BreadcrumbList still shows)
                const scanResponse = await aiSearchAPI.scanSite(selectedSite);
                if (scanResponse.data.success) {
                    setScanResults(scanResponse.data.data);
                } else {
                    setScanResults(prev => ({ ...prev, has_schema_markup: false, schema_source: 'none', articles_with_schema: 0, active_schema_types: [] }));
                }
                setSchemaMessage({ type: 'success', text: 'Schema.org markup has been disabled.' });
            } else {
                setSchemaMessage({ type: 'error', text: response.data.message || 'Failed to remove schema.' });
            }
        } catch (err) {
            setSchemaMessage({ type: 'error', text: 'Failed to remove schema: ' + (err.response?.data?.message || err.message) });
        } finally {
            setAddingSchema(false);
        }
    };

    const handleDownloadLlmsTxt = () => {
        if (!scanResults?.scan_results?.llms_txt?.content && !scanResults?.generated_llms_txt) {
            return;
        }

        const content = scanResults.generated_llms_txt || scanResults.scan_results.llms_txt.content;
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'llms.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-400';
        if (score >= 60) return 'text-blue-400';
        if (score >= 40) return 'text-amber-400';
        return 'text-rose-400';
    };

    const getScoreBg = (score) => {
        if (score >= 80) return 'bg-emerald-500/20 border-emerald-500/30';
        if (score >= 60) return 'bg-blue-500/20 border-blue-500/30';
        if (score >= 40) return 'bg-amber-500/20 border-amber-500/30';
        return 'bg-rose-500/20 border-rose-500/30';
    };

    const getPriorityColor = (priority) => {
        if (priority === 'high') return 'bg-rose-500/20 text-rose-400 border-rose-500/30';
        if (priority === 'medium') return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    };

    return (
        <div className="text-zinc-50">
            <main className="flex-1 p-8 overflow-y-auto custom-scrollbar">
                    <div className="max-w-7xl mx-auto space-y-3">

                        {/* Search / Site selector bar — top, pill style matching other pages */}
                        <div className="relative z-20">
                            <div className="bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-3 flex items-center gap-3 shadow-lg shadow-black/30 flex-wrap">
                                <div className="flex items-center gap-2 shrink-0">
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center">
                                        <RocketIcon className="w-4 h-4 text-violet-400" />
                                    </div>
                                </div>
                                <Select value={selectedSite} onValueChange={setSelectedSite}>
                                    <SelectTrigger className="flex-1 min-w-[220px] bg-zinc-900/50 border-white/10 h-9">
                                        <SelectValue placeholder="Select a WordPress site to scan…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {loading ? (
                                            <SelectItem value="loading" disabled>Loading...</SelectItem>
                                        ) : wordPressSites.length === 0 ? (
                                            <SelectItem value="none" disabled>No sites found</SelectItem>
                                        ) : (
                                            wordPressSites.map(site => (
                                                <SelectItem key={site.id} value={site.id.toString()}>
                                                    {site.site_name}
                                                </SelectItem>
                                            ))
                                        )}
                                    </SelectContent>
                                </Select>
                                <Button
                                    onClick={handleScanSite}
                                    disabled={!selectedSite || scanning}
                                    className="bg-violet-600 hover:bg-violet-700 h-9 shrink-0"
                                >
                                    {scanning ? (
                                        <><ReloadIcon className="w-4 h-4 mr-2 animate-spin" />Scanning...</>
                                    ) : (
                                        <><MagnifyingGlassIcon className="w-4 h-4 mr-2" />Scan Site</>
                                    )}
                                </Button>
                            </div>

                            {wordPressSites.length === 0 && !loading && (
                                <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-300 text-sm flex items-center gap-2">
                                    <InfoCircledIcon className="w-4 h-4 shrink-0" />
                                    No WordPress sites connected. Please add a site first.
                                </div>
                            )}
                        </div>


                        {/* Error Display */}
                        {error && (
                            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 flex items-center gap-3">
                                <InfoCircledIcon className="w-5 h-5" />
                                {error}
                            </div>
                        )}


                        {/* Empty State — shown before any scan */}
                        {!scanResults && !scanning && (
                            <div className="space-y-6 mt-2">
                                {/* Hero message */}
                                <div className="text-center space-y-2 max-w-xl mx-auto">
                                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-violet-500/20 to-blue-500/20 border border-white/10">
                                        <RocketIcon className="w-8 h-8 text-violet-400" />
                                    </div>
                                    <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight text-white">Optimize Your Site for AI Search</h2>
                                    <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium">Get cited in AI-generated answers across all major AI models</p>
                                    <div className="flex flex-wrap justify-center gap-2 pt-1">
                                        {['ChatGPT', 'Perplexity', 'Claude', 'Gemini', 'Copilot', 'Meta AI', 'Grok'].map(ai => (
                                            <span key={ai} className="px-3 py-1 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-zinc-400">
                                                {ai}
                                            </span>
                                        ))}
                                    </div>
                                    <p className="text-zinc-400 text-sm leading-relaxed">
                                        Select a WordPress site above and hit <span className="text-white font-medium">Scan Site</span> to get a full AI readiness report — schema, llms.txt, semantic HTML, and more.
                                    </p>
                                </div>

                                {/* Feature cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                                    <div className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-5 hover:border-violet-500/30 transition-all hover:shadow-lg hover:shadow-violet-500/10">
                                        <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                            <BarChartIcon className="w-5 h-5 text-violet-400" />
                                        </div>
                                        <h3 className="text-sm font-semibold text-white mb-1">AI Readiness Score</h3>
                                        <p className="text-xs text-zinc-400 leading-relaxed">Get a 0–100 score showing how well your site is optimised for ChatGPT, Perplexity, Claude, and Google Gemini.</p>
                                    </div>

                                    <div className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-5 hover:border-blue-500/30 transition-all hover:shadow-lg hover:shadow-blue-500/10">
                                        <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                            <FileTextIcon className="w-5 h-5 text-blue-400" />
                                        </div>
                                        <h3 className="text-sm font-semibold text-white mb-1">llms.txt File</h3>
                                        <p className="text-xs text-zinc-400 leading-relaxed">Check if your site has an llms.txt — the emerging standard that tells AI crawlers what content they can and cannot read.</p>
                                    </div>

                                    <div className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-5 hover:border-emerald-500/30 transition-all hover:shadow-lg hover:shadow-emerald-500/10">
                                        <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                            <CodeIcon className="w-5 h-5 text-emerald-400" />
                                        </div>
                                        <h3 className="text-sm font-semibold text-white mb-1">Schema.org Markup</h3>
                                        <p className="text-xs text-zinc-400 leading-relaxed">Detect and manage structured data types like WebSite, Article, and BreadcrumbList so AI and Google understand your content.</p>
                                    </div>

                                    <div className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-xl p-5 hover:border-amber-500/30 transition-all hover:shadow-lg hover:shadow-amber-500/10">
                                        <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                            <StackIcon className="w-5 h-5 text-amber-400" />
                                        </div>
                                        <h3 className="text-sm font-semibold text-white mb-1">Semantic HTML</h3>
                                        <p className="text-xs text-zinc-400 leading-relaxed">Analyse heading structure, landmark elements, and content quality — signals AI models rely on to understand and cite your pages.</p>
                                    </div>
                                </div>


                                <div className="text-center">
                                    <p className="text-zinc-600 text-xs flex items-center justify-center gap-2">
                                        <InfoCircledIcon className="w-3.5 h-3.5" />
                                        Scans are instant and do not affect your live site
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Scan Results */}
                        {scanResults && (
                            <div className="space-y-6 animate-fade-in-up">


                                {/* AI Readiness Score */}
                                <Card className="border-white/10 bg-zinc-900/40 backdrop-blur-xl">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <BarChartIcon className="w-5 h-5 text-violet-400" />
                                            AI Readiness Score
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="flex items-center justify-center">
                                            <div className={`w-32 h-32 rounded-full border-4 ${getScoreBg(scanResults.ai_readiness_score)} flex items-center justify-center`}>
                                                <div className="text-center">
                                                    <div className={`text-4xl font-bold ${getScoreColor(scanResults.ai_readiness_score)}`}>
                                                        {scanResults.ai_readiness_score}
                                                    </div>
                                                    <div className="text-xs text-zinc-400">/ 100</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                                                {scanResults.has_llms_txt ? (
                                                    <CheckCircledIcon className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                                                ) : (
                                                    <CrossCircledIcon className="w-6 h-6 text-rose-400 mx-auto mb-2" />
                                                )}
                                                <div className="text-xs text-zinc-400">llms.txt</div>
                                            </div>
                                            <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                                                {scanResults.has_schema_markup ? (
                                                    <CheckCircledIcon className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                                                ) : (
                                                    <CrossCircledIcon className="w-6 h-6 text-rose-400 mx-auto mb-2" />
                                                )}
                                                <div className="text-xs text-zinc-400">Schema.org</div>
                                            </div>
                                            <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                                                {scanResults.allows_ai_crawlers ? (
                                                    <CheckCircledIcon className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                                                ) : (
                                                    <CrossCircledIcon className="w-6 h-6 text-rose-400 mx-auto mb-2" />
                                                )}
                                                <div className="text-xs text-zinc-400">AI Crawlers</div>
                                            </div>
                                            <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                                                {scanResults.uses_semantic_html ? (
                                                    <CheckCircledIcon className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                                                ) : (
                                                    <CrossCircledIcon className="w-6 h-6 text-rose-400 mx-auto mb-2" />
                                                )}
                                                <div className="text-xs text-zinc-400">Semantic HTML</div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* robots.txt AI Bot Access Card */}
                                {scanResults.robots_txt && (
                                    <Card className="border-white/10 bg-zinc-900/40 backdrop-blur-xl">
                                        <CardHeader>
                                            <div className="flex items-center justify-between">
                                                <CardTitle className="flex items-center gap-2">
                                                    <FileTextIcon className="w-5 h-5 text-blue-400" />
                                                    AI Bot Access (robots.txt)
                                                    <span className="relative group inline-flex items-center">
                                                        <InfoCircledIcon className="w-3.5 h-3.5 text-zinc-500 cursor-help" />
                                                        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 text-xs text-zinc-300 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50 leading-relaxed">
                                                            Allow or disallow AI crawlers access to your site. Allowing them helps your content appear in AI-powered answers (ChatGPT, Perplexity, Gemini), driving more organic visibility and traffic.
                                                        </span>
                                                    </span>
                                                </CardTitle>
                                                {!scanResults.robots_txt.fetched ? (
                                                    <span className="text-xs text-zinc-500 px-2 py-1 rounded-full bg-zinc-800 border border-white/5">Not found</span>
                                                ) : scanResults.robots_txt.all_allowed ? (
                                                    <span className="flex items-center gap-1.5 text-xs text-emerald-400 px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                                                        <CheckCircledIcon className="w-3.5 h-3.5" /> All bots allowed
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1.5 text-xs text-rose-400 px-2 py-1 rounded-full bg-rose-500/10 border border-rose-500/20">
                                                        <CrossCircledIcon className="w-3.5 h-3.5" /> Bots blocked
                                                    </span>
                                                )}
                                            </div>
                                            <CardDescription>
                                                {scanResults.robots_txt.fetched
                                                    ? <>Checked <a href={scanResults.robots_txt.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{scanResults.robots_txt.url}</a></>
                                                    : 'Could not fetch robots.txt — AI bots are allowed to crawl by default when no file exists.'
                                                }
                                            </CardDescription>
                                        </CardHeader>

                                        {scanResults.robots_txt.fetched && (
                                            <CardContent className="space-y-3">
                                                {/* Single toggle action pill */}
                                                <div>
                                                    {robotsMessage && (
                                                        <p className={`text-xs mb-2 ${robotsMessage.type === 'success' ? 'text-emerald-400' : 'text-rose-400'}`}>
                                                            {robotsMessage.text}
                                                        </p>
                                                    )}
                                                    {scanResults.robots_txt.all_allowed ? (
                                                        <button
                                                            disabled={robotsFixing}
                                                            onClick={async () => {
                                                                setRobotsFixing(true);
                                                                setRobotsMessage(null);
                                                                const allAgents = ['GPTBot', 'OAI-SearchBot', 'PerplexityBot', 'ClaudeBot', 'anthropic-ai', 'Google-Extended', 'meta-externalagent'];
                                                                try {
                                                                    const res = await aiSearchAPI.blockRobotsTxt(selectedSite, allAgents);
                                                                    if (res.data.success) {
                                                                        setRobotsMessage({ type: 'success', text: 'All AI bots blocked in robots.txt.' });
                                                                        setScanResults(prev => ({
                                                                            ...prev,
                                                                            allows_ai_crawlers: false,
                                                                            ai_readiness_score: Math.max(0, (prev.ai_readiness_score || 0) - 15),
                                                                            robots_txt: {
                                                                                ...prev.robots_txt,
                                                                                all_allowed: false,
                                                                                blocked_count: allAgents.length,
                                                                                bots: prev.robots_txt.bots.map(b => ({ ...b, status: 'blocked' })),
                                                                            }
                                                                        }));
                                                                    } else {
                                                                        setRobotsMessage({ type: 'error', text: res.data.message });
                                                                    }
                                                                } catch (e) {
                                                                    setRobotsMessage({ type: 'error', text: e?.response?.data?.message || 'Action failed.' });
                                                                } finally {
                                                                    setRobotsFixing(false);
                                                                }
                                                            }}
                                                            className="text-xs font-semibold px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/25 text-rose-400 hover:bg-rose-500/20 transition-colors disabled:opacity-40 cursor-pointer"
                                                        >
                                                            {robotsFixing ? 'Blocking…' : '🚫 Block All AI Bots'}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            disabled={robotsFixing}
                                                            onClick={async () => {
                                                                setRobotsFixing(true);
                                                                setRobotsMessage(null);
                                                                const allAgents = ['GPTBot', 'OAI-SearchBot', 'PerplexityBot', 'ClaudeBot', 'anthropic-ai', 'Google-Extended', 'meta-externalagent'];
                                                                try {
                                                                    const res = await aiSearchAPI.fixRobotsTxt(selectedSite, allAgents);
                                                                    if (res.data.success) {
                                                                        setRobotsMessage({ type: 'success', text: 'All AI bots allowed. Their blocks removed from robots.txt.' });
                                                                        setScanResults(prev => {
                                                                            const wasAllowed = prev.allows_ai_crawlers;
                                                                            return {
                                                                                ...prev,
                                                                                allows_ai_crawlers: true,
                                                                                ai_readiness_score: Math.min(100, (prev.ai_readiness_score || 0) + (wasAllowed ? 0 : 15)),
                                                                                robots_txt: {
                                                                                    ...prev.robots_txt,
                                                                                    all_allowed: true,
                                                                                    blocked_count: 0,
                                                                                    bots: prev.robots_txt.bots.map(b => ({ ...b, status: 'allowed' })),
                                                                                }
                                                                            };
                                                                        });
                                                                    } else {
                                                                        setRobotsMessage({ type: 'error', text: res.data.message });
                                                                    }
                                                                } catch (e) {
                                                                    setRobotsMessage({ type: 'error', text: e?.response?.data?.message || 'Action failed.' });
                                                                } finally {
                                                                    setRobotsFixing(false);
                                                                }
                                                            }}
                                                            className="text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-40 cursor-pointer"
                                                        >
                                                            {robotsFixing ? 'Allowing…' : '✅ Allow All AI Bots'}
                                                        </button>
                                                    )}
                                                </div>
                                            </CardContent>
                                        )}
                                    </Card>
                                )}


                                {/* Heading Structure Card */}
                                {scanResults.heading_structure && scanResults.heading_structure.checked && (
                                    <Card className="border-white/10 bg-zinc-900/40 backdrop-blur-xl">
                                        <CardHeader>
                                            <div className="flex items-center justify-between">
                                                <CardTitle className="flex items-center gap-2">
                                                    <BarChartIcon className="w-5 h-5 text-amber-400" />
                                                    Heading Structure &amp; Answer-First
                                                </CardTitle>
                                                {scanResults.heading_structure.hierarchy_pass_rate >= 80 ? (
                                                    <span className="flex items-center gap-1.5 text-xs text-emerald-400 px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                                                        <CheckCircledIcon className="w-3.5 h-3.5" /> Good
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1.5 text-xs text-amber-400 px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/20">
                                                        <InfoCircledIcon className="w-3.5 h-3.5" /> Needs work
                                                    </span>
                                                )}
                                            </div>
                                            <CardDescription>
                                                Checked {scanResults.heading_structure.total_articles} published article{scanResults.heading_structure.total_articles !== 1 ? 's' : ''} for AI-optimised structure
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="p-4 bg-zinc-800/50 rounded-xl border border-white/5">
                                                    <div className="text-2xl font-bold text-white mb-1">
                                                        {scanResults.heading_structure.hierarchy_pass_rate}%
                                                    </div>
                                                    <div className="text-xs text-zinc-400">Articles with correct H1→H2→H3 hierarchy</div>
                                                    <div className="mt-2 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full transition-all ${scanResults.heading_structure.hierarchy_pass_rate >= 80 ? 'bg-emerald-500' :
                                                                scanResults.heading_structure.hierarchy_pass_rate >= 50 ? 'bg-amber-500' : 'bg-rose-500'
                                                                }`}
                                                            style={{ width: `${scanResults.heading_structure.hierarchy_pass_rate}%` }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="p-4 bg-zinc-800/50 rounded-xl border border-white/5">
                                                    <div className="text-2xl font-bold text-white mb-1">
                                                        {scanResults.heading_structure.answer_first_pass_rate}%
                                                    </div>
                                                    <div className="text-xs text-zinc-400">Articles with answer-first introduction</div>
                                                    <div className="mt-2 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full transition-all ${scanResults.heading_structure.answer_first_pass_rate >= 80 ? 'bg-emerald-500' :
                                                                scanResults.heading_structure.answer_first_pass_rate >= 50 ? 'bg-amber-500' : 'bg-rose-500'
                                                                }`}
                                                            style={{ width: `${scanResults.heading_structure.answer_first_pass_rate}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {scanResults.heading_structure.issues_sample?.length > 0 && (
                                                <div className="space-y-2">
                                                    <p className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Issues found</p>
                                                    {scanResults.heading_structure.issues_sample.map((item, i) => (
                                                        <div key={i} className="p-3 bg-rose-500/5 border border-rose-500/10 rounded-lg">
                                                            <p className="text-xs font-medium text-zinc-300 mb-1 truncate">{item.topic}</p>
                                                            <ul className="space-y-0.5">
                                                                {item.issues.map((issue, j) => (
                                                                    <li key={j} className="text-xs text-rose-400 flex items-start gap-1.5">
                                                                        <CrossCircledIcon className="w-3 h-3 mt-0.5 shrink-0" />{issue}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}

                                {/* No heading data message */}
                                {scanResults.heading_structure && !scanResults.heading_structure.checked && (
                                    <Card className="border-white/10 bg-zinc-900/40 backdrop-blur-xl">
                                        <CardHeader>
                                            <CardTitle className="flex items-center gap-2">
                                                <BarChartIcon className="w-5 h-5 text-zinc-500" />
                                                Heading Structure &amp; Answer-First
                                            </CardTitle>
                                            <CardDescription>No published articles found — generate and publish articles to see your structure score.</CardDescription>
                                        </CardHeader>
                                    </Card>
                                )}


                                {/* E-E-A-T Signals Card */}
                                {scanResults.eeat_signals && (
                                    <Card className="border-white/10 bg-zinc-900/40 backdrop-blur-xl">
                                        <CardHeader>
                                            <div className="flex items-center justify-between">
                                                <CardTitle className="flex items-center gap-2">
                                                    <CheckCircledIcon className="w-5 h-5 text-violet-400" />
                                                    E-E-A-T Signals
                                                </CardTitle>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-2xl font-bold text-white">{scanResults.eeat_signals.score}%</span>
                                                    {scanResults.eeat_signals.score >= 80 ? (
                                                        <span className="flex items-center gap-1.5 text-xs text-emerald-400 px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                                                            <CheckCircledIcon className="w-3.5 h-3.5" /> Strong
                                                        </span>
                                                    ) : scanResults.eeat_signals.score >= 60 ? (
                                                        <span className="flex items-center gap-1.5 text-xs text-amber-400 px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/20">
                                                            <InfoCircledIcon className="w-3.5 h-3.5" /> Fair
                                                        </span>
                                                    ) : (
                                                        <span className="flex items-center gap-1.5 text-xs text-rose-400 px-2 py-1 rounded-full bg-rose-500/10 border border-rose-500/20">
                                                            <CrossCircledIcon className="w-3.5 h-3.5" /> Weak
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <CardDescription>
                                                {scanResults.eeat_signals.passed} of {scanResults.eeat_signals.total} trust signals passed — signals AI models use to decide if your site is worth citing
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-3">
                                                {Object.values(scanResults.eeat_signals.signals || {}).map((signal, i) => (
                                                    <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${signal.pass
                                                            ? 'bg-emerald-500/5 border-emerald-500/10'
                                                            : 'bg-rose-500/5 border-rose-500/10'
                                                        }`}>
                                                        <div className={`mt-0.5 shrink-0 ${signal.pass ? 'text-emerald-400' : 'text-rose-400'
                                                            }`}>
                                                            {signal.pass
                                                                ? <CheckCircledIcon className="w-4 h-4" />
                                                                : <CrossCircledIcon className="w-4 h-4" />
                                                            }
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className={`text-xs font-semibold mb-0.5 ${signal.pass ? 'text-emerald-300' : 'text-rose-300'
                                                                }`}>{signal.label}</p>
                                                            <p className="text-xs text-zinc-400 leading-relaxed">{signal.detail}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}


                                {/* Improvement Suggestions */}
                                {scanResults.improvement_suggestions && scanResults.improvement_suggestions.length > 0 && (
                                    <Card className="border-white/10 bg-zinc-900/40 backdrop-blur-xl">
                                        <CardHeader>
                                            <CardTitle className="flex items-center gap-2">
                                                <LightningBoltIcon className="w-5 h-5 text-amber-400" />
                                                Improvement Suggestions
                                            </CardTitle>
                                            <CardDescription>Actionable steps to improve your AI search ranking</CardDescription>
                                        </CardHeader>
                                        <CardContent className="space-y-3">
                                            {scanResults.improvement_suggestions.map((suggestion, index) => (
                                                <div key={index} className="p-4 bg-zinc-800/50 rounded-lg border border-white/5">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <h4 className="font-semibold text-white">{suggestion.title}</h4>
                                                        <Badge className={getPriorityColor(suggestion.priority)}>
                                                            {suggestion.priority}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm text-zinc-400 mb-2">{suggestion.description}</p>
                                                    <div className="text-xs text-emerald-400 font-medium">
                                                        Impact: {suggestion.impact}
                                                    </div>
                                                </div>
                                            ))}
                                        </CardContent>
                                    </Card>
                                )}

                                {/* llms.txt Generator */}
                                <Card className="border-white/10 bg-zinc-900/40 backdrop-blur-xl">
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <CardTitle className="flex items-center gap-2">
                                                    <FileTextIcon className="w-5 h-5 text-blue-400" />
                                                    llms.txt File
                                                </CardTitle>
                                                <CardDescription className="mt-1">
                                                    Helps AI platforms understand your content structure
                                                </CardDescription>
                                            </div>
                                            {scanResults.has_llms_txt ? (
                                                scanResults.llms_source === 'contentvertex' ? (
                                                    <span className="text-xs text-emerald-400 flex items-center gap-2 shrink-0">
                                                        <CheckCircledIcon className="w-4 h-4" /> Active
                                                        <button onClick={handleRemoveLlmsTxt} disabled={addingLlms} className={`text-rose-400 hover:text-rose-300 text-xs ${addingLlms ? '' : 'underline'}`}>
                                                            {addingLlms ? 'Removing...' : 'Remove'}
                                                        </button>
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-emerald-400 flex items-center gap-1 shrink-0">
                                                        <CheckCircledIcon className="w-4 h-4" /> Active
                                                    </span>
                                                )
                                            ) : (
                                                <Button
                                                    onClick={handleAddLlmsTxt}
                                                    disabled={addingLlms}
                                                    size="sm"
                                                    className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3"
                                                >
                                                    {addingLlms ? 'Adding...' : '+ Add'}
                                                </Button>
                                            )}
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        {!scanResults.has_llms_txt && (
                                            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-300 text-sm">
                                                <CrossCircledIcon className="w-4 h-4 inline mr-2" />
                                                llms.txt is not found on your site. Click Add to create it.
                                            </div>
                                        )}
                                        {!scanResults.has_llms_txt && scanResults.generated_llms_txt && (
                                            <div className="space-y-3">
                                                <div className="bg-zinc-950 border border-white/10 rounded-lg p-4 max-h-48 overflow-y-auto custom-scrollbar">
                                                    <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono">
                                                        {scanResults.generated_llms_txt}
                                                    </pre>
                                                </div>
                                                <Button onClick={handleDownloadLlmsTxt} variant="outline" className="w-full">
                                                    <DownloadIcon className="w-4 h-4 mr-2" />
                                                    Download llms.txt
                                                </Button>
                                            </div>
                                        )}
                                        {llmsMessage && (
                                            <div className={`p-3 rounded-lg text-sm ${llmsMessage.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300' : 'bg-rose-500/10 border border-rose-500/20 text-rose-300'}`}>
                                                {llmsMessage.text}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>


                                {/* Schema.org Markup */}
                                <Card className="border-white/10 bg-zinc-900/40 backdrop-blur-xl">
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <CardTitle className="flex items-center gap-2">
                                                    <CodeIcon className="w-5 h-5 text-violet-400" />
                                                    Schema.org Markup
                                                </CardTitle>
                                                <CardDescription className="mt-1">
                                                    {scanResults.articles_with_schema} of {scanResults.total_articles} articles have structured data
                                                    {scanResults.active_schema_types?.length > 0
                                                        ? <> for <strong className="text-zinc-300">{scanResults.active_schema_types.join(', ')}</strong></>
                                                        : null
                                                    }
                                                </CardDescription>
                                                {/* Warn about missing important types */}
                                                {(() => {
                                                    const active = scanResults.active_schema_types || [];
                                                    const important = [
                                                        { type: 'WebSite', reason: 'helps Google identify your site name and URL' },
                                                    ];
                                                    const missing = important.filter(i => !active.includes(i.type));
                                                    if (!missing.length) return null;
                                                    return (
                                                        <div className="mt-2 space-y-1">
                                                            {missing.map(({ type, reason }) => (
                                                                <p key={type} className="text-xs text-amber-400/90 flex items-start gap-1">
                                                                    <span className="shrink-0">⚠️</span>
                                                                    <span><strong>{type}</strong> type is not added — it {reason}. Consider adding it below.</span>
                                                                </p>
                                                            ))}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                            {/* External schema — badge only */}
                                            {scanResults.schema_source === 'external' && (
                                                <span className="text-xs text-emerald-400 flex items-center gap-2 shrink-0">
                                                    <CheckCircledIcon className="w-4 h-4" /> Active
                                                </span>
                                            )}
                                            {/* CV schema — Active + Remove */}
                                            {scanResults.schema_source === 'contentvertex' && (
                                                <span className="text-xs text-emerald-400 flex items-center gap-2 shrink-0">
                                                    <CheckCircledIcon className="w-4 h-4" /> Active
                                                    <button onClick={handleRemoveSchema} disabled={addingSchema} className={`text-rose-400 hover:text-rose-300 text-xs ${addingSchema ? '' : 'underline'}`}>
                                                        {addingSchema ? 'Removing...' : 'Remove'}
                                                    </button>
                                                </span>
                                            )}
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        {/* External schema info — only shown when a known SEO plugin manages schema */}
                                        {scanResults.schema_source === 'external' && scanResults.schema_plugin_name && (
                                            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-300 text-sm">
                                                <CheckCircledIcon className="w-4 h-4 inline mr-2" />
                                                Schema is managed by <strong>{scanResults.schema_plugin_name}</strong>. ContentVertex cannot modify it.
                                            </div>
                                        )}

                                        {/* Schema type selector */}
                                        {(() => {
                                            const activeTypes = scanResults.active_schema_types || [];
                                            const isExternalPlugin = scanResults.schema_source === 'external' && !!scanResults.schema_plugin_name;
                                            const allTypes = [
                                                { type: 'WebSite', label: 'WebSite', desc: 'Site name & search', locked: true, tooltip: 'Tells Google your site name and URL. Add this if you want your site to appear with a sitelinks search box in Google — it is always recommended.' },
                                                { type: 'Article', label: 'Article', desc: 'Blog post data', locked: false, tooltip: 'Adds headline, author, and publish date to each post. Add this if you run a blog or news site — it helps Google and AI models like ChatGPT understand your content better.' },
                                                { type: 'Organization', label: 'Organization', desc: 'Brand & logo', locked: false, tooltip: 'Adds your brand name and logo to search results. Add this if you represent a business or brand and want to build authority in Google Knowledge Panels.' },
                                                { type: 'BreadcrumbList', label: 'BreadcrumbList', desc: 'Navigation path', locked: false, tooltip: 'Shows the page path (e.g. Home  Category  Post) in search results. Add this if your site has categories and you want users to see the navigation trail in Google snippets.' },
                                                { type: 'FAQPage', label: 'FAQPage', desc: 'Q&A sections', locked: false, tooltip: 'Marks Q&A pairs so AI models can extract them directly. Add this if your posts include FAQ sections — it helps ChatGPT, Perplexity, and Google understand your answers.' },
                                                { type: 'Person', label: 'Person', desc: 'Author pages', locked: false, tooltip: 'Adds author name, bio, and social links to author archive pages. Add this if your site has multiple writers and you want to build author trust and authority.' },
                                            ];
                                            const selectableTypes = selectedSchemaTypes.filter(t => !activeTypes.includes(t));
                                            return (
                                                <div className="space-y-3">
                                                    <p className="text-xs text-zinc-400">
                                                        {(scanResults.schema_source === 'contentvertex' || scanResults.schema_source === 'external') ? 'Schema types status:' : 'Select the schema types to enable:'}
                                                    </p>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        {allTypes.map(({ type, label, desc, locked, tooltip }) => {
                                                            const alreadyActive = activeTypes.includes(type);
                                                            const isDisabled = locked || alreadyActive || isExternalPlugin;
                                                            const isChecked = alreadyActive || selectedSchemaTypes.includes(type);
                                                            return (
                                                                <div key={type} className="relative group">
                                                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                                                                        <div className="bg-zinc-800 border border-white/10 text-zinc-300 text-xs rounded-lg px-3 py-2 shadow-xl leading-relaxed">{tooltip}</div>
                                                                        <div className="w-2 h-2 bg-zinc-800 border-r border-b border-white/10 rotate-45 mx-auto -mt-1" />
                                                                    </div>
                                                                    <label className={`flex items-start gap-2 p-2 rounded-lg border w-full transition-colors ${alreadyActive ? 'border-emerald-500/30 bg-emerald-500/5 cursor-default' : isChecked ? 'border-violet-500/50 bg-violet-500/10 cursor-pointer' : isDisabled ? 'border-white/5 bg-zinc-800/20 cursor-not-allowed opacity-50' : 'border-white/10 bg-zinc-800/30 hover:border-white/20 cursor-pointer'}`}>
                                                                        <input type="checkbox" checked={isChecked} onChange={() => !isDisabled && toggleSchemaType(type)} disabled={isDisabled} className="mt-0.5 accent-violet-500 shrink-0" />
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="text-xs font-medium text-white truncate">{label}</div>
                                                                            {alreadyActive ? <div className="text-xs text-emerald-400"> Already added</div> : <div className="text-xs text-zinc-500">{desc}</div>}
                                                                        </div>
                                                                    </label>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    {!isExternalPlugin && selectableTypes.length > 0 && (
                                                        <Button onClick={handleAddSchema} disabled={addingSchema} size="sm" className="w-full bg-violet-600 hover:bg-violet-700 text-white text-xs">
                                                            {addingSchema ? 'Enabling...' : `+ Add ${selectableTypes.length} new type${selectableTypes.length > 1 ? 's' : ''}`}
                                                        </Button>
                                                    )}
                                                    {!isExternalPlugin && selectableTypes.length > 0 && (
                                                        <p className="text-right text-zinc-500 italic" style={{ fontSize: '10px', lineHeight: '1.4' }}>
                                                            ⚠️ Only add types relevant to your site. Google may penalise incorrect or empty schema markup.
                                                        </p>
                                                    )}
                                                    {!isExternalPlugin && selectableTypes.length === 0 && activeTypes.length > 0 && (
                                                        <p className="text-xs text-emerald-400 text-center">All selected types are already active </p>
                                                    )}
                                                </div>
                                            );
                                        })()}

                                        {schemaMessage && (
                                            <div className={`p-3 rounded-lg text-sm ${schemaMessage.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300' : 'bg-rose-500/10 border border-rose-500/20 text-rose-300'}`}>
                                                {schemaMessage.text}
                                            </div>
                                        )}

                                    </CardContent>
                                </Card>


                                {/* Detailed Scan Results */}
                                <Card className="border-white/10 bg-zinc-900/40 backdrop-blur-xl">
                                    <CardHeader>
                                        <CardTitle>Detailed Scan Results</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="p-4 bg-zinc-800/50 rounded-lg">
                                                    <div className="text-sm text-zinc-400 mb-1">Semantic HTML Score</div>
                                                    <div className="text-2xl font-bold text-white">{scanResults.semantic_score}/100</div>
                                                </div>
                                                <div className="p-4 bg-zinc-800/50 rounded-lg">
                                                    <div className="text-sm text-zinc-400 mb-1">Content Quality Score</div>
                                                    <div className="text-2xl font-bold text-white">{scanResults.content_quality_score}/100</div>
                                                </div>
                                            </div>

                                            {scanResults.suggestions && scanResults.suggestions.length > 0 && (
                                                <div>
                                                    <h3 className="text-sm font-semibold text-white mb-3">Recommendations</h3>
                                                    <div className="space-y-2">
                                                        {scanResults.suggestions.map((s, i) => (
                                                            <div key={i} className="p-3 bg-zinc-800/50 rounded-lg flex items-start gap-3">
                                                                <div className={`text-xs px-2 py-0.5 rounded-full shrink-0 mt-0.5 ${s.priority === 'High' ? 'bg-rose-500/20 text-rose-300' : s.priority === 'Medium' ? 'bg-amber-500/20 text-amber-300' : 'bg-zinc-600/20 text-zinc-400'}`}>{s.priority}</div>
                                                                <div>
                                                                    <div className="text-sm font-medium text-white">{s.title}</div>
                                                                    <div className="text-xs text-zinc-400 mt-0.5">{s.description}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>

                            </div>
                        )}
                    </div>
                </main>
        </div>
    );
}

export default AISearchOptimizer;
